import type { ResourceModelConfig } from '@bryntum/schedulerpro';
import {
  Role as RoleT,
  User as UserT,
  Team as TeamT,
  Company as CompanyT,
  UserType,
} from 'api/models';
import { ProjectBudgetItem, ProjectForRm } from 'api/project';
import { StatusFieldOption } from 'api/tasks';

export type ResourceModelConfigWithoutMandatoryFields = Omit<
  ResourceModelConfig,
  'id'
>;
export enum SchedulerResourceType {
  Project = 'project',
  Role = 'role',
  ExpandableRole = 'expandable_role',
  User = 'user',
  Team = 'team',
  Company = 'company',
  TeamHeader = 'team_header',
  PartnerHeader = 'partner_header',
  AssignProject = 'assign_project',
  AssignBudget = 'assign_budget',
  AssignUserOrPlaceholder = 'assign_user_or_placeholder',
  DemandRole = 'demand_role',
  DemandCapacity = 'demand_capacity',
  TotalAllocation = 'total_allocation',
  UserTotalAllocation = 'user_total_allocation',
  UserTotalSoftAllocation = 'user_total_soft_allocation',
  UserTotalHardAllocation = 'user_total_hard_allocation',
  TeamTotalAllocation = 'team_total_allocation',
  TeamTotalSoftAllocation = 'team_total_soft_allocation',
  TeamTotalHardAllocation = 'team_total_hard_allocation',
  UserFte = 'user_fte',
  PartnerFte = 'partner_fte',
  TimeOffFte = 'timeoff_fte',
  HolidayFte = 'holiday_fte',
  CapacityAllocationDiffFte = 'capacity_allocation_diff_fte',
  DemandAllocationProject = 'demand_allocation_project',
  ShowAllProjects = 'show_all_projects',
  OtherProjects = 'other_projects',
  OtherTeamMembers = 'other_team_members',
  Loading = 'loading',
  LoadMoreProjects = 'load_more_projects',
  FinancialBudget = 'financial_budget',
}
interface BaseResource
  extends Partial<ResourceModelConfigWithoutMandatoryFields> {
  id: string;
}
type ResourceWithUserRelatedFields = BaseResource &
  Pick<UserT, 'userId' | 'userType' | 'userStatus' | 'company'>;
export interface AssignProjectRow extends ResourceWithUserRelatedFields {
  type: SchedulerResourceType.AssignProject;
  userId: number;
  alreadyAssignedProjectIds: number[];
}
export interface AssignBudgetRow extends ResourceWithUserRelatedFields {
  type: SchedulerResourceType.AssignBudget;
  userId: number;
  projectId: number;
  roleId?: number;
  referenceTeam?: TeamT;
  alreadyAssignedBudgetIds: number[];
}
export interface ProjectsUnderTeamLoaderRow extends BaseResource {
  type: SchedulerResourceType.Loading;
  roleId: number;
  companyId?: number;
  page?: number;
}
export interface ProjectsUnderPeopleLoaderRow extends BaseResource {
  type: SchedulerResourceType.Loading;
  userId: number;
  page?: number;
}

export interface PeopleLoaderRow extends BaseResource {
  type: SchedulerResourceType.Loading;
  page?: number;
}

export interface ResourceUnderProjectLoaderRow extends BaseResource {
  type: SchedulerResourceType.Loading;
  projectId: number;
  page?: number;
  projectFinancialsBudgetId?: number;
}

export interface ProjectLoaderRow extends BaseResource {
  type: SchedulerResourceType.Loading;
  page: number;
}
export interface AssignUserOrPlaceholderRow extends BaseResource {
  type: SchedulerResourceType.AssignUserOrPlaceholder;
  projectId: number;
  projectFinancialsBudgetId?: number;
}

type WithProjectFieldsForLeaf = BaseResource &
  Pick<
    ProjectForRm,
    'projectName' | 'projectId' | 'customer' | 'autoAllocation'
  >;
export interface ProjectAsLeafResource extends WithProjectFieldsForLeaf {
  type: SchedulerResourceType.Project;
  userId: number;
  roleId?: number;
  referenceTeam?: TeamT;
  isUserProjectOwner: boolean;
  startDate?: string;
  dueDate?: string;
  userTeamMembershipId?: number;
  showAllocations?: boolean;
}

export interface ProjectResourceForPeople extends ProjectAsLeafResource {
  children?: (BudgetAsLeafResource | AssignBudgetRow)[];
}

export interface BudgetAsLeafResource extends WithBudgetFields {
  type: SchedulerResourceType.FinancialBudget;
  userId: number;
  roleId?: number;
  referenceTeam?: TeamT;
  autoAllocation?: boolean;
  startDate?: string;
  dueDate?: string;
  budgetedMinutes?: number;
  totalAllocation: number;
  projectFinancialsBudgetId: number;
  showAllocations?: boolean;
}

export interface BudgetAsLeafResourceForTeams extends WithBudgetFields {
  type: SchedulerResourceType.FinancialBudget;
  teamId: number;
  roleId?: number;
  companyId?: number;
  autoAllocation?: boolean;
  startDate?: string;
  dueDate?: string;
  budgetedMinutes?: number;
  totalAllocation: number;
  projectFinancialsBudgetId: number;
  showAllocations?: boolean;
}

type WithUserFields = BaseResource &
  Pick<
    UserT,
    | 'userId'
    | 'firstName'
    | 'lastName'
    | 'photoUrl'
    | 'role'
    | 'company'
    | 'userType'
    | 'userStatus'
  >;
export interface UserAsResource extends WithUserFields {
  type: SchedulerResourceType.User;
  children: (
    | ProjectAsLeafResource
    | AssignProjectRow
    | ProjectsUnderPeopleLoaderRow
  )[];
}

export interface UserAsLeafResource extends WithUserFields {
  type: SchedulerResourceType.User;
  projectId: number;
  isProjectOwner: boolean;
  totalAllocation: number;
  autoAllocation?: boolean;
  roleId?: number;
  referenceTeam?: TeamT;
  projectFinancialsBudgetId?: number;
  showAllocations?: boolean;
}

type WithTeamFields = BaseResource &
  Pick<TeamT, 'teamId' | 'teamName' | 'roleId' | 'userList' | 'companyList'>;

export interface TeamAsLeafResource extends WithTeamFields {
  type: SchedulerResourceType.Team;
  projectId: number;
  totalAllocation: number;
  autoAllocation?: boolean;
  startDate?: string;
  dueDate?: string;
  isAdditionalHours?: boolean;
  projectFinancialsBudgetId?: number;
  showAllocations?: boolean;
}

export interface TeamAsBudgetResource extends WithTeamFields {
  type: SchedulerResourceType.Team;
  projectId: number;
  projectName: string;
  customer: CompanyT;
  autoAllocation?: boolean;
  startDate?: string;
  dueDate?: string;
  children: BudgetAsLeafResourceForTeams[];
  projectTeamId?: number;
}

type WithRoleFields = BaseResource & Pick<RoleT, 'roleId' | 'roleName'>;
export interface RoleAsResource<
  T extends UserAsResource | UserAsLeafResource | TeamAsLeafResource,
> extends WithRoleFields {
  type: SchedulerResourceType.Role;
  children: T[];
}

type WithBudgetFields = BaseResource & {
  projectFinancialsBudget: ProjectBudgetItem;
  dueDate?: string;
  dataLoading?: boolean;
  projectId?: number;
} & Pick<
    ProjectBudgetItem,
    | 'startDate'
    | 'endDate'
    | 'rateCardVersionId'
    | 'projectMinutes'
    | 'financialContractType'
    | 'projectCustomRates'
  >;

export interface BudgetAsResource<
  T extends
    | UserAsResource
    | UserAsLeafResource
    | TeamAsLeafResource
    | AssignUserOrPlaceholderRow
    | ResourceUnderProjectLoaderRow,
> extends WithBudgetFields {
  type: SchedulerResourceType.FinancialBudget;
  projectFinancialsBudgetId: number;
  children: T[];
  budgetedMinutes?: number;
  autoAllocation: boolean;
  isRateCardEnabled?: boolean;
  totalAllocation: number;
}

export type WithProjectFields = BaseResource &
  Pick<
    ProjectForRm,
    | 'projectName'
    | 'projectId'
    | 'customer'
    | 'budgetedHours'
    | 'budgetedMinutes'
    | 'startDate'
    | 'autoAllocation'
  >;

export interface ProjectAsResource extends WithProjectFields {
  type: SchedulerResourceType.Project;
  totalAllocation: number;
  status?: StatusFieldOption;
  children: (
    | RoleAsResource<UserAsLeafResource | TeamAsLeafResource>
    | BudgetAsResource<
        | UserAsLeafResource
        | TeamAsLeafResource
        | AssignUserOrPlaceholderRow
        | ResourceUnderProjectLoaderRow
      >
    | AssignUserOrPlaceholderRow
    | ResourceUnderProjectLoaderRow
  )[];
}

export type TeamsWithProjectAsLeafResource = Omit<
  TeamAsLeafResource,
  'totalAllocation'
> &
  Omit<WithProjectFields, 'budgetedHours' | 'startDate'>;

type BaseResourceAndRoleInfo = BaseResource &
  Pick<RoleT, 'roleName' | 'roleId'>;

export interface TeamWithProjectGroupedByRoles extends BaseResourceAndRoleInfo {
  type: SchedulerResourceType.ExpandableRole;
  teamCount: number;
  companyId?: number;
  children: (
    | TeamsWithProjectAsLeafResource
    | ProjectsUnderTeamLoaderRow
    | TeamAsBudgetResource
  )[];
}
export interface TeamsAsResource extends BaseResource {
  type: SchedulerResourceType.TeamHeader;
  children: TeamWithProjectGroupedByRoles[];
}
type BaseResourceAndCompanyInfo = BaseResource &
  Pick<CompanyT, 'companyId' | 'companyName' | 'companyLogoUrl'>;

export interface CompanyAsResource extends BaseResourceAndCompanyInfo {
  type: SchedulerResourceType.Company;
  children: (UserAsResource | TeamWithProjectGroupedByRoles)[];
}

export interface PartnerHeader extends BaseResource {
  type: SchedulerResourceType.PartnerHeader;
  children: CompanyAsResource[];
}

export interface BaseDemandResource extends BaseResource {
  roleId: number;
}

export interface ShowAllProjectRow extends BaseDemandResource {
  type: SchedulerResourceType.ShowAllProjects;
  extraProjects: number[];
}

export interface DemandCapacity extends BaseDemandResource {
  type: SchedulerResourceType.DemandCapacity;
}
export interface UserFte extends BaseDemandResource {
  type: SchedulerResourceType.UserFte;
}
export interface PartnerFte extends BaseDemandResource {
  type: SchedulerResourceType.PartnerFte;
}
export interface TimeOffFte extends BaseDemandResource {
  type: SchedulerResourceType.TimeOffFte;
}
export interface HolidayFte extends BaseDemandResource {
  type: SchedulerResourceType.HolidayFte;
}
export interface DemandCapacityAllocationDiffFte extends BaseDemandResource {
  type: SchedulerResourceType.CapacityAllocationDiffFte;
}

export interface DemandAllocationProjectLeafResource
  extends Omit<WithProjectFieldsForLeaf, 'autoAllocation'> {
  type: SchedulerResourceType.DemandAllocationProject;
  roleId: number;
  startDate?: string;
  dueDate?: string;
}

export interface TotalAllocation extends BaseDemandResource {
  type: SchedulerResourceType.TotalAllocation;
  children: (DemandAllocationProjectLeafResource | ShowAllProjectRow)[];
  projectIds: number[];
}

export type UserCountByUserType = Map<UserType, number>;

export interface DemandRole extends BaseDemandResource {
  type: SchedulerResourceType.DemandRole;
  roleName: string;
  userCount: number;
  userCountByUserType: UserCountByUserType;
  children: (
    | DemandCapacity
    | TotalAllocation
    | DemandCapacityAllocationDiffFte
  )[];
}
